import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FiMapPin, FiMoreHorizontal } from 'react-icons/fi';
import { IoBed, IoBedOutline } from 'react-icons/io5';
import { FaBath } from 'react-icons/fa';

// Reusable Listing Card Component
const ListingCard = ({ listing, imageErrors, onImageError, formatPrice, getPropertyTypeDisplay }) => (
  <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
    {/* Property Image */}
    <div className="relative h-48 bg-gray-200">
      <Image
        src={imageErrors[listing.id] ? '/images/placeholder-property.svg' : listing.primaryImage}
        alt={listing.title}
        fill
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        className="object-cover"
        onError={() => onImageError(listing.id)}
      />
      <div className="absolute top-3 left-3">
        <span className="bg-wine text-white px-2 py-1 rounded text-xs font-medium">
          {getPropertyTypeDisplay(listing.type)}
        </span>
      </div>
      <div className="absolute top-3 right-3">
        <span className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm font-semibold">
          {formatPrice(listing.price)}
        </span>
      </div>
    </div>

    {/* Property Details */}
    <div className="p-4">
      <h3 className="font-semibold text-lg mb-2 line-clamp-2">
        {listing.title}
      </h3>
      
      <div className="flex items-center text-gray-600 text-sm mb-3">
        <FiMapPin className="h-4 w-4 mr-1 flex-shrink-0" />
        <span className="truncate">
          {listing.city && listing.state 
            ? `${listing.city}, ${listing.state}`
            : listing.address || 'Location not specified'
          }
        </span>
      </div>

      {/* Property Features */}
      {(listing.bedrooms || listing.bathrooms) && (
        <div className="flex items-center space-x-4 text-gray-600 text-sm mb-3">
          {listing.bedrooms && (
            <div className="flex items-center">
              <IoBedOutline className="h-4 w-4 mr-1" />
              <span>{listing.bedrooms} bed{listing.bedrooms > 1 ? 's' : ''}</span>
            </div>
          )}
          {listing.bathrooms && (
            <div className="flex items-center">
              <FaBath className="h-4 w-4 mr-1" />
              <span>{listing.bathrooms} bath{listing.bathrooms > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}

      {/* Description Preview */}
      {listing.description && (
        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
          {listing.description}
        </p>
      )}

      {/* View Details Button */}
      <Link 
        href={`/listings/${listing.id}`}
        className="w-full bg-wine text-white py-2 px-4 rounded hover:bg-opacity-90 transition-colors inline-block text-center text-sm font-medium"
      >
        View Details
      </Link>
    </div>
  </div>
);

const SearchResults = ({ 
  results = [], 
  fallbackResults = [],
  searchMeta = null,
  loading = false, 
  error = null, 
  pagination = null,
  onLoadMore = () => {},
  searchParams = {},
  noResultsMessage = null
}) => {
  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = (listingId) => {
    setImageErrors(prev => ({
      ...prev,
      [listingId]: true
    }));
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    
    if (price >= 1000000000) {
      return `₦${(price / 1000000000).toFixed(1)}B`;
    }
    if (price >= 1000000) {
      return `₦${(price / 1000000).toFixed(1)}M`;
    }
    if (price >= 1000) {
      return `₦${(price / 1000).toFixed(0)}K`;
    }
    return `₦${price.toLocaleString()}`;
  };

  const getPropertyTypeDisplay = (type) => {
    if (!type) return 'Property';
    
    const typeMap = {
      'apartment': 'Apartment',
      'house': 'House',
      'duplex': 'Duplex',
      'flat': 'Flat',
      'bungalow': 'Bungalow',
      'land': 'Land',
      'commercial': 'Commercial',
      'office': 'Office Space',
      'warehouse': 'Warehouse'
    };
    
    return typeMap[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-900 mb-2 font-semibold">Search Error</div>
        <p className="text-wine-dark mb-4">
          {error.includes('503') 
            ? "Search service is temporarily unavailable. Please try again in a moment."
            : error.includes('Failed to fetch')
            ? "Unable to connect to search service. Please check your internet connection."
            : error
          }
        </p>
        <div className="space-x-3">
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-900 text-white rounded hover:bg-wine-dark transition-colors"
          >
            Try Again
          </button>
          <Link
            href="/listings"
            className="inline-block px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Browse All Listings
          </Link>
        </div>
      </div>
    );
  }

  if (!loading && results.length === 0) {
    const hasSearchParams = Object.values(searchParams).some(param => param?.toString().trim());
    
    if (!hasSearchParams) {
      return null; // Don't show anything if no search has been performed
    }

    // Show fallback results if available
    if (fallbackResults && fallbackResults.length > 0) {
      return (
        <div className="space-y-6">
          {/* No exact matches message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <div className="text-blue-800 font-medium mb-2">
              {searchMeta?.suggestionMessage || "No exact matches found"}
            </div>
            <p className="text-blue-600 text-sm">
              Here are some recent properties you might be interested in:
            </p>
          </div>

          {/* Fallback results grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fallbackResults.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                imageErrors={imageErrors}
                onImageError={handleImageError}
                formatPrice={formatPrice}
                getPropertyTypeDisplay={getPropertyTypeDisplay}
              />
            ))}
          </div>

          {/* Browse all listings suggestion */}
          <div className="text-center">
            <Link 
              href="/listings" 
              className="inline-block px-6 py-3 bg-wine text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Browse All Properties
            </Link>
          </div>
        </div>
      );
    }

    // No results and no fallback
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <div className="text-gray-800 font-medium mb-2 text-lg">No properties found</div>
        <p className="text-gray-600 mb-6">
          {noResultsMessage || "We couldn't find any properties matching your search criteria."}
        </p>
        
        <div className="bg-white rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">Search Tips:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-wine mr-2">•</span>
                Try broader search terms (e.g., "Lagos" instead of "Victoria Island")
              </li>
              <li className="flex items-start">
                <span className="text-wine mr-2">•</span>
                Check your spelling and try alternative spellings
              </li>
            </ul>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-wine mr-2">•</span>
                Remove some filters to expand your search
              </li>
              <li className="flex items-start">
                <span className="text-wine mr-2">•</span>
                Try searching in nearby areas or neighborhoods
              </li>
            </ul>
          </div>
        </div>

        <div className="space-x-3">
          <Link 
            href="/listings" 
            className="inline-block px-6 py-3 bg-wine text-white rounded-lg hover:bg-wine/90 transition-colors font-medium"
          >
            Browse All Properties
          </Link>
          <Link
            href="/search"
            className="inline-block px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Advanced Search
          </Link>
        </div>

        {/* Popular Search Suggestions */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-800 mb-3">Popular Searches:</h4>
          <div className="flex flex-wrap gap-2 justify-center">
            {['Lagos apartments', 'Abuja houses', 'Port Harcourt land', 'Ibadan duplex'].map((suggestion) => (
              <Link
                key={suggestion}
                href={`/listings?q=${encodeURIComponent(suggestion)}`}
                className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
              >
                {suggestion}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Summary */}
      {pagination && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <p>
            Showing {results.length} of {pagination.totalResults} properties
            {searchMeta?.searchType === 'fallback' && (
              <span className="ml-2 text-blue-600 font-medium">
                (showing suggestions)
              </span>
            )}
          </p>
          {loading && (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-wine"></div>
              <span>Searching...</span>
            </div>
          )}
        </div>
      )}

      {/* Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {results.map((listing) => (
          <ListingCard
            key={listing.id}
            listing={listing}
            imageErrors={imageErrors}
            onImageError={handleImageError}
            formatPrice={formatPrice}
            getPropertyTypeDisplay={getPropertyTypeDisplay}
          />
        ))}
      </div>

      {/* Load More Button */}
      {pagination?.hasNextPage && (
        <div className="text-center">
          <button
            onClick={() => onLoadMore(pagination.currentPage + 1)}
            disabled={loading}
            className="px-6 py-3 bg-wine text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Load More Properties'}
          </button>
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && results.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-300"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                <div className="h-8 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
